import axios, { AxiosRequestConfig } from "axios";
import { AuthManager } from "./AuthManager";

export interface IRequestParameters {
  url: string;
  requiresToken?: boolean;
  isUpload?: boolean;
  body?: any;
  options?: any;
  responseType?: "blob" | "arraybuffer" | "json" | "text";
}

export interface IRequestParametersExtra {
  url: string;
  requiresToken?: boolean;
  isUpload?: boolean;
  body?: any;
  options?: any;
  responseType?: "blob" | "arraybuffer" | "json" | "text";
  timeout?: number;
}

export interface IApiClient {
  get<T>(parameters: IRequestParameters): Promise<T>;
  post<T>(parameters: IRequestParameters): Promise<T>;
  post_timeout<T>(parameters: IRequestParametersExtra): Promise<T>;
  put<T>(parameters: IRequestParameters): Promise<T>;
  delete<T>(parameters: IRequestParameters): Promise<T>;
  download(parameters: IRequestParameters): Promise<any>;
}

export interface IApiClientError {
  processed: boolean;
  details: any;
  response: any;
}

export class ApiClient implements IApiClient {
  constructor(
    private apiRoot: string,
    private authManager: AuthManager,
    public onSessionEnd?: any,
    public onNetworkError?: any,
    public onServerError?: any,
    public onAccessDenied?: any
  ) {}

  get<T>(parameters: IRequestParameters): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // extract the individual parameters
      const { url, requiresToken = true } = parameters;

      const options = this.prepareOptions(requiresToken);

      // finally execute the GET request with axios:
      axios
        .get(this.apiRoot + url, options)
        .then((response: any) => {
          resolve(response.data as T);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  post<T>(parameters: IRequestParameters): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const {
        url,
        body: payload,
        requiresToken = true,
        isUpload = false,
      } = parameters;

      const options = this.prepareOptions(requiresToken, isUpload);

      // finally execute the POST request with axios:
      axios
        .post(this.apiRoot + url, payload, options)
        .then((response: any) => {
          resolve(response.data as T);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  post_timeout<T>(parameters: IRequestParametersExtra): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const {
        url,
        body: payload,
        requiresToken = true,
        isUpload = false,
        timeout = 60000, // Default timeout of 60 seconds
      } = parameters;

      const options = this.prepareOptions(requiresToken, isUpload);

      // Add timeout to the options
      const axiosOptions = {
        ...options,
        timeout, // Axios will abort the request if it takes longer than this
      };

      // Finally execute the POST request with axios:
      axios
        .post(this.apiRoot + url, payload, axiosOptions)
        .then((response: any) => {
          resolve(response.data as T);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  put<T>(parameters: IRequestParameters): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const { url, body: payload, requiresToken = true } = parameters;

      const options = this.prepareOptions(requiresToken);

      // finally execute the PUT request with axios:
      axios
        .put(this.apiRoot + url, payload, options)
        .then((response: any) => {
          resolve(response.data as T);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  delete<T>(parameters: IRequestParameters): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // extract the individual parameters
      const { url, requiresToken = true } = parameters;

      const options = this.prepareOptions(requiresToken);

      // finally execute the DELETE request with axios:
      axios
        .delete(this.apiRoot + url, options)
        .then((response: any) => {
          resolve(response.data as T);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  download(parameters: IRequestParameters): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      // extract the individual parameters
      const { url, requiresToken = true } = parameters;

      const options = this.prepareOptions(requiresToken);
      options.responseType = "arraybuffer";
      options.timeout = 5000;

      // finally execute the GET request with axios:
      axios
        .get(this.apiRoot + url, options)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: any) => {
          const processed = this.errorDefaults(error);
          reject({ processed: processed, details: error });
        });
    });
  }

  private prepareOptions = (
    requiresToken: boolean,
    isUpload: boolean = false
  ): AxiosRequestConfig => {
    // prepare headers
    const headers = Object();
    // add header for authentication
    if (requiresToken) {
      headers.Authorization = "Bearer " + this.authManager.getToken();
    }
    // change content type to multipart/form-data to enable binary data upload
    if (isUpload) {
      headers["Content-Type"] = "multipart/form-data";
    }

    // axios request options like headers etc
    const options: AxiosRequestConfig = {
      headers: headers,
      timeout: 5000,
    };

    return options;
  };

  // process defaults for most common errors
  // return TRUE if any defaults have been processed
  private errorDefaults = (error: any) => {
    if (!error.response && this.onNetworkError) {
      this.onNetworkError();
      return true;
    } else if (
      error.response &&
      error.response.status === 500 &&
      this.onServerError
    ) {
      this.onServerError();
      return true;
    } else if (
      error.response &&
      error.response.status === 401 &&
      this.onSessionEnd
    ) {
      this.onSessionEnd();
      return true;
    } else if (
      error.response &&
      error.response.status === 403 &&
      this.onAccessDenied
    ) {
      this.onAccessDenied();
      return true;
    }

    return false;
  };
}
